/**
 * @file Server-side ICS generation service for the calendar library.
 * This service handles iCalendar feed generation and requires Node.js.
 */

import ical from 'ical-generator';
import { getEventsByCalendar } from './events';
import type { Calendar } from '../database/entities';

/**
 * Generates an iCalendar (.ics) feed for a given calendar.
 * This is a server-side operation that requires Node.js.
 *
 * @param calendar The calendar object.
 * @param calendarId The ID of the calendar to generate the feed for.
 * @returns A promise that resolves with the iCalendar feed as a string.
 */
export async function generateIcs(calendar: Calendar, calendarId: string): Promise<string> {
  const cal = ical({ 
    name: calendar.name,
    description: calendar.description,
    timezone: 'Australia/Sydney' // Default to Australian timezone
  });
  
  const events = await getEventsByCalendar(calendarId);
  
  events.forEach(event => {
    const icalEvent = cal.createEvent({
      start: event.start,
      end: event.end,
      summary: event.summary,
      description: event.description,
      location: event.venue ? [
        event.venue.name,
        event.venue.address,
        event.venue.city,
        event.venue.state,
        event.venue.country
      ].filter(Boolean).join(', ') : undefined,
    });

    // Add musician-specific properties as extended properties
    if (event.type) {
      icalEvent.x('X-EVENT-TYPE', event.type);
    }
    
    if (event.genre) {
      icalEvent.x('X-GENRE', event.genre);
    }
    
    if (event.instrument) {
      icalEvent.x('X-INSTRUMENT', event.instrument);
    }
    
    if (event.paymentStatus) {
      icalEvent.x('X-PAYMENT-STATUS', event.paymentStatus);
    }
    
    if (event.status) {
      icalEvent.x('X-STATUS', event.status);
    }

    // Add contact information if available
    if (event.primaryContact) {
      icalEvent.x('X-CONTACT-NAME', event.primaryContact.name);
      if (event.primaryContact.email) {
        icalEvent.x('X-CONTACT-EMAIL', event.primaryContact.email);
      }
      if (event.primaryContact.phone) {
        icalEvent.x('X-CONTACT-PHONE', event.primaryContact.phone);
      }
    }

    // Add recurrence rule if present
    if (event.recurrenceRule) {
      icalEvent.repeating(event.recurrenceRule);
    }
  });

  return cal.toString();
}

/**
 * Generates an ICS feed for a specific event type (e.g., only gigs).
 */
export async function generateIcsByType(
  calendar: Calendar, 
  calendarId: string, 
  eventType: string
): Promise<string> {
  const cal = ical({ 
    name: `${calendar.name} - ${eventType.charAt(0).toUpperCase() + eventType.slice(1)}s`,
    description: `${eventType} events from ${calendar.name}`,
    timezone: 'Australia/Sydney'
  });
  
  const events = await getEventsByCalendar(calendarId);
  const filteredEvents = events.filter(event => event.type === eventType);
  
  filteredEvents.forEach(event => {
    const icalEvent = cal.createEvent({
      start: event.start,
      end: event.end,
      summary: event.summary,
      description: event.description,
      location: event.venue ? [
        event.venue.name,
        event.venue.address,
        event.venue.city,
        event.venue.state,
        event.venue.country
      ].filter(Boolean).join(', ') : undefined,
    });

    // Add extended properties
    icalEvent.x('X-EVENT-TYPE', event.type);
    
    if (event.genre) icalEvent.x('X-GENRE', event.genre);
    if (event.instrument) icalEvent.x('X-INSTRUMENT', event.instrument);
    if (event.paymentStatus) icalEvent.x('X-PAYMENT-STATUS', event.paymentStatus);
    if (event.status) icalEvent.x('X-STATUS', event.status);
    
    if (event.primaryContact) {
      icalEvent.x('X-CONTACT-NAME', event.primaryContact.name);
      if (event.primaryContact.email) icalEvent.x('X-CONTACT-EMAIL', event.primaryContact.email);
      if (event.primaryContact.phone) icalEvent.x('X-CONTACT-PHONE', event.primaryContact.phone);
    }

    if (event.recurrenceRule) {
      icalEvent.repeating(event.recurrenceRule);
    }
  });

  return cal.toString();
}

/**
 * Generates an ICS feed for upcoming events only (next 30 days).
 */
export async function generateUpcomingIcs(calendar: Calendar, calendarId: string): Promise<string> {
  const cal = ical({ 
    name: `${calendar.name} - Upcoming Events`,
    description: `Upcoming events from ${calendar.name}`,
    timezone: 'Australia/Sydney'
  });
  
  const events = await getEventsByCalendar(calendarId);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  
  const upcomingEvents = events.filter(event => 
    event.start >= now && event.start <= thirtyDaysFromNow
  );
  
  upcomingEvents.forEach(event => {
    const icalEvent = cal.createEvent({
      start: event.start,
      end: event.end,
      summary: event.summary,
      description: event.description,
      location: event.venue ? [
        event.venue.name,
        event.venue.address,
        event.venue.city,
        event.venue.state,
        event.venue.country
      ].filter(Boolean).join(', ') : undefined,
    });

    // Add extended properties
    icalEvent.x('X-EVENT-TYPE', event.type);
    
    if (event.genre) icalEvent.x('X-GENRE', event.genre);
    if (event.instrument) icalEvent.x('X-INSTRUMENT', event.instrument);
    if (event.paymentStatus) icalEvent.x('X-PAYMENT-STATUS', event.paymentStatus);
    if (event.status) icalEvent.x('X-STATUS', event.status);
    
    if (event.primaryContact) {
      icalEvent.x('X-CONTACT-NAME', event.primaryContact.name);
      if (event.primaryContact.email) icalEvent.x('X-CONTACT-EMAIL', event.primaryContact.email);
      if (event.primaryContact.phone) icalEvent.x('X-CONTACT-PHONE', event.primaryContact.phone);
    }

    if (event.recurrenceRule) {
      icalEvent.repeating(event.recurrenceRule);
    }
  });

  return cal.toString();
} 